import { Flex, Icon, Text } from '@chakra-ui/react';
import { Select } from '@document/common/Select';
import { Clock } from 'lucide-react';

interface DelaySelectProps {
  value: number;
  onChange: (value: number) => void;
}

interface DelayOption {
  value: number;
  label: string;
}

const DELAY_OPTIONS: DelayOption[] = [
  { value: 0, label: 'No delay' },
  { value: 30, label: '30 minutes' },
  { value: 60, label: '1 hour' },
  { value: 120, label: '2 hours' },
  { value: 240, label: '4 hours' },
  { value: 480, label: '8 hours' },
  { value: 1440, label: '1 day' },
  { value: 2880, label: '2 days' },
];

const darkSelectStyles = {
  container: (provided: Record<string, unknown>) => ({
    ...provided,
    minWidth: '250px',
  }),
  option: (
    provided: Record<string, unknown>,
    state: { isSelected: boolean; isFocused: boolean }
  ) => ({
    ...provided,
    bg: state.isSelected
      ? 'whiteAlpha.200'
      : state.isFocused
        ? 'whiteAlpha.100'
        : 'transparent',
    color: 'white',
    _hover: { bg: 'whiteAlpha.100' },
  }),
  singleValue: (provided: Record<string, unknown>) => ({
    ...provided,
    color: 'white',
  }),
};

export const DelaySelect = ({ value, onChange }: DelaySelectProps) => {
  const selectedOption = DELAY_OPTIONS.find((opt) => opt.value === value);

  const formatOptionLabel = (option: DelayOption) => (
    <Flex alignItems="center" gap={2}>
      <Icon as={Clock} boxSize={4} color="whiteAlpha.600" />
      <Text color="white">{option.label}</Text>
    </Flex>
  );

  return (
    <Flex
      alignItems="center"
      borderBottom="1px solid"
      borderColor="whiteAlpha.100"
      justifyContent="space-between"
      py={5}
    >
      <Text color="whiteAlpha.700">Delay</Text>
      <Select<DelayOption>
        chakraStyles={darkSelectStyles}
        formatOptionLabel={formatOptionLabel}
        getOptionLabel={(opt) => opt.label}
        getOptionValue={(opt) => String(opt.value)}
        isSearchable={false}
        options={DELAY_OPTIONS}
        value={selectedOption}
        onChange={(newValue) => {
          if (newValue) {
            onChange(newValue.value);
          }
        }}
      />
    </Flex>
  );
};
